/**
 * Product carousel thumbnails.
 *
 * Click handling is delegated on this element so CDP slide swaps do not stack
 * listeners. Keep play/3D icon markup here; syncThumbnailsWithSlides rebuilds thumbs
 * after head/shaft/grip/headcover updates.
 */
const VIDEO_THUMB_PLAY_ICON =
  '<span class="carousel-thumb__play" aria-hidden="true"><span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" class="icon icon-play" viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg></span></span>';

const MODEL_THUMB_ICON =
  '<span class="carousel-thumb__play carousel-thumb__model" aria-hidden="true"><span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-3d-model" viewBox="0 0 18 21"><path fill="currentColor" d="m7.68 20.629-6.4-3.906A2.71 2.71 0 0 1 0 14.405v-7.81a2.72 2.72 0 0 1 1.278-2.318l6.4-3.9a2.52 2.52 0 0 1 2.644-.003l6.4 3.9A2.72 2.72 0 0 1 18 6.6v7.809a2.71 2.71 0 0 1-1.279 2.318l-6.4 3.906A2.54 2.54 0 0 1 9 21a2.54 2.54 0 0 1-1.32-.371M8.724 2.078l-6.4 3.9a.71.71 0 0 0-.321.612v7.81a.71.71 0 0 0 .32.61L8 18.48v-7.561a1.77 1.77 0 0 1 .934-1.555l6.512-3.523L9.28 2.08a.52.52 0 0 0-.556-.002"/></svg></span></span>';

function isVideoMediaType(mediaType) {
  return mediaType === 'video' || mediaType === 'external_video';
}

export class ProductCarouselThumbsComponent extends HTMLElement {
  constructor() {
    super();
    this.carousel = null;
    this.thumbnails = [];
    this.scrollHandler = null;
    this.onThumbClick = this.onThumbClick.bind(this);
  }

  connectedCallback() {
    this.findCarousel();

    if (!this.carousel) {
      console.warn('product-carousel-thumbs: No product-carousel found with matching data-carousel-id');
      return;
    }

    this.thumbnails = Array.from(this.querySelectorAll('[data-thumbnail-slide-id]'));

    if (this.thumbnails.length === 0) {
      console.warn('product-carousel-thumbs: No thumbnail buttons found');
      return;
    }

    // Delegated so CDP thumbnail rebuilds do not stack click handlers.
    this.addEventListener('click', this.onThumbClick);
    this.setupScrollListener();
    this.updateActiveThumbnail();
    this.setupMutationObserver();
  }

  onThumbClick(event) {
    const thumb = event.target.closest('[data-thumbnail-slide-id]');
    if (!thumb || !this.contains(thumb)) return;

    const slideId = thumb.getAttribute('data-thumbnail-slide-id');
    if (slideId && this.carousel) {
      this.carousel.scrollToSlide(slideId);
    }
  }


  findCarousel() {
    const carouselId = this.getAttribute('data-carousel-id');
    if (carouselId) {
      const carousel = document.querySelector(`product-carousel[data-carousel-id="${carouselId}"]`);
      if (carousel) {
        this.carousel = carousel;
      }
    }
  }

  setupScrollListener() {
    if (!this.carousel?.track) return;

    let scrollTimeout = null;
    this.scrollHandler = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.updateActiveThumbnail();
      }, 100);
    };

    this.carousel.track.addEventListener('scroll', this.scrollHandler);
  }

  updateActiveThumbnail() {
    if (!this.carousel) return;

    const currentSlideId = this.carousel.getCurrentSlideId();

    this.thumbnails.forEach(thumb => {
      const slideId = thumb.getAttribute('data-thumbnail-slide-id');
      if (slideId === currentSlideId) {
        thumb.classList.add('carousel-thumb--active');
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.classList.remove('carousel-thumb--active');
        thumb.removeAttribute('aria-current');
      }
    });
  }

  setupMutationObserver() {
    // Watch for slides being added/removed in the carousel
    if (!this.carousel?.track) return;

    this.mutationObserver = new MutationObserver(() => {
      this.thumbnails = Array.from(this.querySelectorAll('[data-thumbnail-slide-id]'));
      this.updateActiveThumbnail();
    });

    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true
    });

    // Also watch the carousel track for slide changes
    this.carouselMutationObserver = new MutationObserver(() => {
      this.syncThumbnailsWithSlides();
    });

    this.carouselMutationObserver.observe(this.carousel.track, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Sync thumbnails with carousel slides
   * Updates thumbnail URLs and creates/removes thumbnails as needed
   * Maintains the same order as slides appear in the carousel
   */
  syncThumbnailsWithSlides() {
    if (!this.carousel?.track) return;

    // Get slides in their current order from the carousel track
    const slides = Array.from(this.carousel.track.querySelectorAll('[data-slide-id][data-thumbnail-url]'));
    const slideIds = new Set();
    const thumbnailMap = new Map();

    // First, collect all existing thumbnails into a map
    const existingThumbs = Array.from(this.querySelectorAll('[data-thumbnail-slide-id]'));
    existingThumbs.forEach((thumb) => {
      const slideId = thumb.getAttribute('data-thumbnail-slide-id');
      if (slideId) {
        thumbnailMap.set(slideId, thumb);
      }
    });

    // Process slides in order and create/update thumbnails
    slides.forEach((slide) => {
      const slideId = slide.getAttribute('data-slide-id');
      const thumbnailUrl = slide.getAttribute('data-thumbnail-url');

      if (!slideId || !thumbnailUrl) return;

      slideIds.add(slideId);

      let thumbButton = thumbnailMap.get(slideId);

      if (!thumbButton) {
        // Create new thumbnail
        thumbButton = document.createElement('button');
        thumbButton.type = 'button';
        thumbButton.setAttribute('data-thumbnail-slide-id', slideId);
        thumbButton.className = 'carousel-thumb';
        thumbnailMap.set(slideId, thumbButton);
      }

      const mediaType = slide.getAttribute('data-media-type');
      const ariaLabel = slide.getAttribute('data-thumbnail-label');
      const isVideo = isVideoMediaType(mediaType);
      const isModel = mediaType === 'model';

      thumbButton.classList.toggle('carousel-thumb--video', isVideo);
      thumbButton.classList.toggle('carousel-thumb--model', isModel);
      if (ariaLabel) {
        thumbButton.setAttribute('aria-label', ariaLabel);
      }

      // Update thumbnail image
      let img = thumbButton.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        thumbButton.prepend(img);
      }

      if (img.src !== thumbnailUrl) {
        img.src = thumbnailUrl;
        img.srcset = `${thumbnailUrl} 1x`;
        img.loading = 'lazy';
        img.alt = '';
      }

      const mediaIcon = thumbButton.querySelector('.carousel-thumb__play');
      const iconMarkup = isVideo ? VIDEO_THUMB_PLAY_ICON : isModel ? MODEL_THUMB_ICON : '';
      const hasMatchingIcon = mediaIcon && mediaIcon.classList.contains('carousel-thumb__model') === isModel;
      if (mediaIcon && (!iconMarkup || !hasMatchingIcon)) {
        mediaIcon.remove();
      }
      if (iconMarkup && !hasMatchingIcon) {
        thumbButton.insertAdjacentHTML('beforeend', iconMarkup);
      }
    });

    // Remove all existing thumbnails
    existingThumbs.forEach((thumb) => {
      thumb.remove();
    });

    // Re-add thumbnails in the same order as slides
    slides.forEach((slide) => {
      const slideId = slide.getAttribute('data-slide-id');
      const thumbButton = thumbnailMap.get(slideId);
      if (thumbButton) {
        this.appendChild(thumbButton);
      }
    });

    // Remove thumbnails for slides that no longer exist
    thumbnailMap.forEach((thumb, slideId) => {
      if (!slideIds.has(slideId)) {
        thumb.remove();
      }
    });

    // Re-query thumbnails and update handlers
    this.thumbnails = Array.from(this.querySelectorAll('[data-thumbnail-slide-id]'));
    this.updateActiveThumbnail();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onThumbClick);
    if (this.scrollHandler && this.carousel?.track) {
      this.carousel.track.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this.carouselMutationObserver) {
      this.carouselMutationObserver.disconnect();
    }
  }
}

// Auto-register the component if not already registered
if (!customElements.get('product-carousel-thumbs')) {
  customElements.define('product-carousel-thumbs', ProductCarouselThumbsComponent);
}

